import { Completion } from '@youversion/analytics-youversion-nextjs';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import { useCurrency } from 'utils/store/currency';
import { useFrequency } from 'utils/store/frequency';
import { useFund } from 'utils/store/fund';
import { usePaymentMethod } from 'utils/store/paymentMethod';

const DEFAULT_ANALYTICS_LANGUAGE = 'en-US';

interface Params {
  currencyErrorMessage: string;
}

export function useTrackGiveCompletion({ currencyErrorMessage }: Params) {
  const { getFrequencyName, selectedFrequency } = useFrequency(DEFAULT_ANALYTICS_LANGUAGE);
  const { getFundName, selectedFund } = useFund(DEFAULT_ANALYTICS_LANGUAGE);
  const { selectedCurrency } = useCurrency();
  const { paymentMethodType } = usePaymentMethod();
  const { i18n } = useTranslation('common');

  const trackGiveCurrencyError = useCallback(() => {
    Completion.Gift.fire({
      completionContext: 'give_intent',
      currency: selectedCurrency,
      elementId: 'give_btn',
      error: i18n.getFixedT('en-US')(currencyErrorMessage),
      frequency: getFrequencyName(selectedFrequency),
      fund: getFundName(selectedFund),
      paymentMethod: paymentMethodType ?? 'none',
      success: false,
    });
  }, [
    currencyErrorMessage,
    getFrequencyName,
    getFundName,
    i18n,
    paymentMethodType,
    selectedCurrency,
    selectedFrequency,
    selectedFund,
  ]);

  const trackGiveSuccess = useCallback(() => {
    Completion.Gift.fire({
      completionContext: 'give_intent',
      currency: selectedCurrency,
      elementId: 'give_btn',
      frequency: getFrequencyName(selectedFrequency),
      fund: getFundName(selectedFund),
      paymentMethod: paymentMethodType ?? 'none',
      success: true,
    });
  }, [getFrequencyName, getFundName, paymentMethodType, selectedCurrency, selectedFrequency, selectedFund]);

  return { trackGiveCurrencyError, trackGiveSuccess };
}
